/**
 * One entry point for "what will this bot do this turn": situation → plan →
 * pre-computed solutions for the plan's first fire / jump / move. Runs in the
 * worker (with the WASM batch scorer) and as the main-thread fallback; both
 * paths call this so they cannot drift apart.
 *
 * Every mode, deathmatch included, produces a step list (botBehaviorGraphs);
 * the runner executes steps and never looks at the mode.
 */

import { dacos } from '../core/dmath';
import { INTERACT_RANGE } from '../constants';
import { vecNormalize } from '../math';
import { clampMoveTarget } from '../core/actions';
import type { PlayerState, GameState, BotConfig, BotFogContext, BotTurnSteps, TransitGraph, GameMode, ModeConfig } from '../types';
import { hasUnlimitedJumpFuel } from '../core/modeRules';
import type { BotFireResult, BotMoveResult, BotJumpResult, BatchScorer } from './bot';
import { computeBotFire, computeBotMove, computeBotJump } from './bot';
import { computeGuardMove } from './bot/guard';
import { computeSneakMove, sneakWalkApplies } from './bot/sneak';

import { buildBotSituation, planBotTurn } from './botBehaviorGraphs';
import { FIRE_MOVEMENT_COST, JUMPPACK_VELOCITY_BONUS } from '../constants';
import { jumpVelocityBonusOf } from '../core/actions';
import type { SeededRandom } from '../core/random';

/** A planned turn with the expensive searches already done. */
export interface BotTurnPlan {
  plan: BotTurnSteps;
  fireResult: BotFireResult | null;
  moveResult: BotMoveResult | null;
  jumpResult: BotJumpResult | null;
  rngState: number;
}

/**
 * Jump velocity bonus used when building the transit graph. Objective modes
 * have unlimited jumppack fuel, so the bonus always applies; in deathmatch it
 * applies only while the bot still has fuel.
 */
export function transitFuelBonus(player: PlayerState, mode: GameMode | ModeConfig): number {
  // Extra launch velocity beyond movement × JUMP_VELOCITY_PER_MOVEMENT: fuel, plus the kit's jump bonus (Phase 48)
  const kit = jumpVelocityBonusOf(player);
  if (hasUnlimitedJumpFuel(mode)) return JUMPPACK_VELOCITY_BONUS + kit;
  return (player.jumppackFuel > 0 ? JUMPPACK_VELOCITY_BONUS : 0) + kit;
}

/**
 * Plan the turn and pre-compute the first fire, jump and move the plan asks
 * for. Fire steps marked `fresh` are solved at execution time (the bot will
 * have moved) and are skipped here. A jump that follows a fire is searched
 * with the movement the fire will have spent.
 */
export function computeTurnPlan(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext: BotFogContext | undefined,
  transitGraph: TransitGraph,
  scorer?: BatchScorer,
): BotTurnPlan {
  const situation = buildBotSituation(player, gameState, config, fogContext, transitGraph);
  const plan = planBotTurn(player, gameState, situation, config, rng, transitGraph, fogContext);

  let fireResult: BotFireResult | null = null;
  let moveResult: BotMoveResult | null = null;
  let jumpResult: BotJumpResult | null = null;
  let movementConsumed = 0;

  for (const step of plan.steps) {
    if ((step.type === 'fire' && !step.fresh) || step.type === 'fireProbe') {
      if (!fireResult) {
        fireResult = computeBotFire(player, gameState, config, rng, fogContext, scorer);
        movementConsumed += FIRE_MOVEMENT_COST;
      }
    }
    if (step.type === 'jump' && !jumpResult) {
      // Match the budget the executor will have: a fire before the jump costs movement.
      // The graph was built for the full budget; reachability only shrinks, and
      // computeBotJump fine-tunes with the actual movement anyway.
      const adjustedPlayer = movementConsumed > 0
        ? { ...player, movementRemaining: Math.max(0, player.movementRemaining - movementConsumed) }
        : player;
      jumpResult = computeBotJump(adjustedPlayer, gameState, config, rng, transitGraph, step.toward === 'reinforce' ? situation.alert?.reinforceTarget : undefined, fogContext);
    }
    if (step.type === 'move' && !moveResult && step.toward === 'seat' && situation.seatTarget) {
      // S6: walk onto the seat (clamped to this turn's reach; the seat is within walk + reach by construction)
      // stop at the edge of reach (90 % of it) so the walk leaves movement for the mount and the shot
      const planet = gameState.planets[player.planetIndex];
      const seat = vecNormalize(situation.seatTarget.surfaceDir), here = vecNormalize(player.surfaceDir);
      const total = dacos(Math.max(-1, Math.min(1, here.x * seat.x + here.y * seat.y + here.z * seat.z)));
      const keep = Math.min(total, (INTERACT_RANGE * 0.9) / Math.max(1, planet.radius));
      const t = total > 1e-6 ? (total - keep) / total : 0;
      const dest = vecNormalize({ x: here.x + (seat.x - here.x) * t, y: here.y + (seat.y - here.y) * t, z: here.z + (seat.z - here.z) * t });
      const to = clampMoveTarget(player, planet, dest, gameState.carryables, gameState.config.modeConfig);
      moveResult = to ? { targetDir: vecNormalize(to) } : null;
    }
    if (step.type === 'move' && !moveResult) {
      moveResult = (step.toward === 'patrol' || step.toward === 'investigate')
        ? computeGuardMove(player, gameState, situation, step.toward)
        : sneakWalkApplies(player, gameState, config, fogContext)   // 57.5 / 57.10c: around the cones, when there are cones
          ? computeSneakMove(player, gameState, situation, fogContext)
          : computeBotMove(player, gameState, config, rng, fogContext);
    }
  }

  return { plan, fireResult, moveResult, jumpResult, rngState: rng.getState() };
}
